/**
 * View path utilities for handling multiple paths
 *
 * NOTE: 後方互換性のため、既存のpath: string形式も自動変換する
 */

import type { ComponentConditionEntry } from "../types/TestTarget";

export interface ViewPath {
  path: string;
  [key: string]: any;
}

/**
 * Viewのparamsを正規化してpaths配列を返す
 * 
 * @param params Viewのparamsオブジェクト
 * @returns 正規化されたpaths配列
 */
export function normalizeViewPaths(params: any): ViewPath[] {
  if (!params || typeof params !== "object") {
    return [];
  }

  // paths配列が既にある場合はそのまま返す
  if (Array.isArray(params.paths)) {
    return params.paths.filter((p: any) => p && typeof p === "object" && typeof p.path === "string");
  }

  // path: string形式の場合はpaths配列に変換
  if (typeof params.path === "string") {
    return [{ path: params.path }];
  }

  // どちらもない場合は空配列を返す
  return [];
}

/**
 * Viewのparamsから正規化されたpaths配列を取得
 * 
 * @param params Viewのparamsオブジェクト
 * @returns 正規化されたpaths配列
 */
export function getViewPaths(params: any): ViewPath[] {
  return normalizeViewPaths(params);
}

/**
 * Viewのparamsにpathsを設定する
 * 
 * @param params Viewのparamsオブジェクト
 * @param paths 設定するpaths配列
 * @returns 更新されたparamsオブジェクト（paths形式のみ、pathフィールドは削除）
 */
export function setViewPaths(params: any, paths: ViewPath[]): any {
  if (!params || typeof params !== "object") {
    return { paths };
  }

  const updated = { ...params };
  updated.paths = paths;
  
  // paths形式に統一するため、pathフィールドを削除
  if ("path" in updated) {
    delete updated.path;
  }

  return updated;
}


/**
 * params.conditions を string[] として正規化する。
 *
 * View / Section / Overlay の「表示条件」用。View の共通コンポーネント別表示条件は
 * `getViewComponentConditions` を使う。
 */
export function getParamsConditions(params: unknown): string[] {
  if (!params || typeof params !== "object") {
    return [];
  }
  const raw = (params as { conditions?: unknown }).conditions;
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((c): c is string => typeof c === "string");
}

/**
 * View の params から共通コンポーネント別表示条件を取り出す。
 *
 * 不正な型のエントリ・値はサイレントに除外する。
 */
export function getViewComponentConditions(
  params: unknown
): ComponentConditionEntry[] {
  if (!params || typeof params !== "object") {
    return [];
  }
  const raw = (params as { componentConditions?: unknown }).componentConditions;
  if (!Array.isArray(raw)) {
    return [];
  }
  const entries: ComponentConditionEntry[] = [];
  for (const item of raw) {
    if (!item || typeof item !== "object") {
      continue;
    }
    const obj = item as { componentId?: unknown; conditions?: unknown };
    if (typeof obj.componentId !== "string") {
      continue;
    }
    const conditions = Array.isArray(obj.conditions)
      ? obj.conditions.filter((c): c is string => typeof c === "string")
      : [];
    entries.push({ componentId: obj.componentId, conditions });
  }
  return entries;
}

/** 指定 componentId に紐づく条件配列を取り出す。componentId が空文字なら常に空配列。 */
export function getConditionsForComponentId(
  entries: ComponentConditionEntry[],
  componentId: string
): string[] {
  if (!componentId.trim()) {
    return [];
  }
  return (
    entries.find((e) => e.componentId === componentId)?.conditions ?? []
  );
}

/**
 * 指定 componentId の条件配列を上書きした新しい配列を返す。
 *
 * `nextConditions` が空配列ならエントリ削除、`componentId` が空文字なら入力を変えない。
 */
export function upsertComponentCondition(
  entries: ComponentConditionEntry[],
  componentId: string,
  nextConditions: string[]
): ComponentConditionEntry[] {
  if (!componentId.trim()) {
    return entries;
  }
  const others = entries.filter((e) => e.componentId !== componentId);
  if (nextConditions.length === 0) {
    return others;
  }
  return [...others, { componentId, conditions: nextConditions }];
}

/** 指定 componentId のエントリを削除した新しい配列を返す。空文字なら入力を変えない。 */
export function removeComponentCondition(
  entries: ComponentConditionEntry[],
  componentId: string
): ComponentConditionEntry[] {
  if (!componentId.trim()) {
    return entries;
  }
  return entries.filter((e) => e.componentId !== componentId);
}

/**
 * 行で componentId が切り替わったとき、旧 ID の条件を新 ID に紐替える。
 *
 * 新 ID 空文字 / 旧 ID と同一 / 旧 ID にエントリ無し なら引き継がない。
 * 新 ID と同じキーの既存エントリは捨てて新しい条件で置き換える。
 */
export function swapComponentIdPreservingConditions(
  entries: ComponentConditionEntry[],
  oldId: string,
  newId: string
): ComponentConditionEntry[] {
  if (oldId === newId) {
    return entries;
  }
  const filtered = entries.filter(
    (e) => e.componentId !== oldId && e.componentId !== newId
  );
  const oldEntry = entries.find((e) => e.componentId === oldId);
  if (
    newId.trim().length > 0 &&
    oldEntry &&
    oldEntry.conditions.length > 0
  ) {
    return [
      ...filtered,
      { componentId: newId, conditions: oldEntry.conditions },
    ];
  }
  return filtered;
}

/**
 * 保存直前の正規化: trim、空条件除去、有効な componentId のみに絞り込む。
 *
 * 結果が空なら `null` (クリア信号) を返す。
 */
export function normalizeComponentConditionsForSave(
  entries: ComponentConditionEntry[],
  validComponentIds: string[]
): ComponentConditionEntry[] | null {
  const validIdSet = new Set(validComponentIds);
  const normalized = entries
    .map((e) => ({
      componentId: e.componentId.trim(),
      conditions: e.conditions.filter((c) => c.trim() !== ""),
    }))
    .filter(
      (e) =>
        e.componentId.length > 0 &&
        e.conditions.length > 0 &&
        validIdSet.has(e.componentId)
    );
  return normalized.length > 0 ? normalized : null;
}
